import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseDataProps, OptionsRender } from './types';
import { AllFieldValueTypes, EditDesc } from '90.quickConnect.Models/models';
import { DataSourceType } from '90.quickConnect.Models/enums';
import { useStore } from '30.quickConnect.Stores';
import { useFieldAutoComplete } from '50.quickConnect.Fields/hooks/useFieldAutoComplete';
import useLRU from '50.quickConnect.Fields/LRU/hooks';

const useData = (
  editDesc: EditDesc,
  updateDeclaration: (updatedFieldFullPathId: string, newValue: AllFieldValueTypes) => void,
): UseDataProps => {
  const {
    DeclarationStore: { requiredField, verifRequiredField, fieldFocused, setFieldFocused },
    EquipmentsStore: { deleteIsInDataSource, setIsInDataSource, abortSearchFieldValues },
  } = useStore();
  // On récupère la translation
  const { t } = useTranslation('declaration');
  const { fullPathId, value, dataSourceType, dataSource, lruCount } = editDesc;
  const [autoCompleteProp, setAutoCompleteProp] = useState<OptionsRender[]>([]);
  const [searchAllUO, setSearchAllUO] = useState<boolean>(dataSource?.forceSearchAllUO ?? false);
  const [addFocus] = useState<boolean>(false);

  // Custom Hooks
  const {
    filterLoadedOptions,
    searchOptionsNow,
    cancelSearchNow,
    deleteAutoComplete,
    registerNewFamillySearch,
    registerNewAADSearch,
    registerValidState,
    unRegisterValidState,
  } = useFieldAutoComplete();

  // Pour le LRU
  const {
    lruState: { list, showList },
    displayList,
    hideList,
    deleteLRU,
  } = useLRU({ fullId: fullPathId, lruCount: lruCount ?? 3 });

  const toggleDisplayLruList = useCallback(() => {
    if (showList) hideList();
    else displayList();
  }, [showList, displayList, hideList]);

  // useRefs
  const textFieldRef = useRef();
  const userHasClicked = useRef<boolean>(false);

  // UseMemos
  const disabledField: boolean = useMemo(() => {
    if (!dataSource) return false;

    if (!dataSource.forcedSelection) return false;

    if (typeof value !== 'string') return false;

    return value !== '' && userHasClicked.current === true;
  }, [dataSource, value]);

  const textLocalValue = useMemo(() => {
    return typeof value === 'string' ? value : '';
  }, [value]);

  // End Region useMemo

  // Le champ est demonte (fermeture du formulaire, navigation): la recherche encore en vol devient
  // inutile. On l'annule pour ne pas laisser de requete orpheline ni de trace d'erreur reseau sans objet.
  useEffect(
    () => () => {
      abortSearchFieldValues();
    },
    [abortSearchFieldValues],
  );

  // Clic sur la loupe: seul declencheur de l'appel API pour les sources Azure AD et famille
  const searchNow = useCallback(async () => {
    if (dataSourceType !== DataSourceType.AzureAD && dataSourceType !== DataSourceType.Family) return;

    userHasClicked.current = false;
    const searchResults = await searchOptionsNow(textLocalValue, dataSourceType, dataSource, searchAllUO);
    if (searchResults) setAutoCompleteProp(searchResults);
  }, [dataSourceType, dataSource, searchAllUO, textLocalValue, searchOptionsNow]);

  // Perimetre de recherche modifie: les resultats affiches ne sont plus valables, nouvelle recherche par la loupe
  const handleOnChangeSearchAllUO = useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault();
      setSearchAllUO(!searchAllUO);
      cancelSearchNow();
      setAutoCompleteProp([]);
    },
    [searchAllUO, cancelSearchNow],
  );

  const deleteLRUItem = useCallback(
    async (event: React.SyntheticEvent, valueToRemove: string) => {
      await deleteLRU(valueToRemove);
    },
    [deleteLRU],
  );

  // On définit les callbacks
  const updateGlobalState = useCallback(
    (nextValue: string) => {
      updateDeclaration(fullPathId, nextValue);
    },
    [fullPathId, updateDeclaration],
  );

  const updateState = useCallback(
    (newValue: string) => {
      // Si le champ possède le focus, on le retire
      if (fieldFocused === fullPathId) setFieldFocused(undefined);

      updateGlobalState(newValue);
    },
    [updateGlobalState, fieldFocused, fullPathId, setFieldFocused],
  );

  const outlinedInputChanged = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const {
        target: { value: newValue },
      } = event;
      updateState(newValue);
    },
    [updateState],
  );

  const onClickAutoComplete = useCallback(
    (newValue: string) => {
      if (newValue === '') {
        if (dataSource?.forcedSelection && dataSourceType) {
          deleteIsInDataSource(fullPathId);

          // On delete le internalData correspondant
          deleteAutoComplete(fullPathId, dataSourceType);
          unRegisterValidState(fullPathId);
        }

        // on remet a false le userHasClicked pour permettre l'affichage de la liste
        userHasClicked.current = false;
      } else {
        if (dataSource?.forcedSelection) {
          setIsInDataSource(fullPathId, true);
          registerValidState(fullPathId);
        }

        // Déclenchement de la réf pour éviter de garder l'affichage de la liste ouverte.
        userHasClicked.current = true;

        // On enregistre / Met à jour le internalData correspondant
        if (dataSourceType === DataSourceType.Family) {
          registerNewFamillySearch(fullPathId, newValue);
        } else if (dataSourceType === DataSourceType.AzureAD) {
          registerNewAADSearch(fullPathId, newValue);
        }
      }
      updateState(newValue);
    },
    [
      dataSource?.forcedSelection,
      fullPathId,
      deleteIsInDataSource,
      setIsInDataSource,
      updateState,
      deleteAutoComplete,
      registerNewFamillySearch,
      registerNewAADSearch,
      dataSourceType,
      unRegisterValidState,
      registerValidState,
    ],
  );

  const onChangeAutoComplete = useCallback(
    async (newValue: string) => {
      if (dataSource?.forcedSelection) {
        setIsInDataSource(fullPathId, false);
      }

      updateState(newValue);

      // Cas de la recherche dans les familles ou azureAD (Contacts pas pris en charge): pas d'appel API
      // a la frappe (loupe uniquement), seulement le filtrage des resultats deja charges
      if (dataSourceType === DataSourceType.AzureAD || dataSourceType === DataSourceType.Family)
        setAutoCompleteProp(
          userHasClicked.current ? [] : filterLoadedOptions(newValue, dataSourceType, dataSource, searchAllUO),
        );
    },
    [dataSource, updateState, setIsInDataSource, fullPathId, filterLoadedOptions, dataSourceType, searchAllUO],
  );

  const autocompleteChanged = useCallback(
    (event: React.SyntheticEvent, newValue: string | OptionsRender | null, reason: string) => {
      const next = typeof newValue === 'string' ? newValue : newValue?.value ?? '';

      // équivalent de "newValue === ''"
      if (reason === 'clear' || next === '') {
        userHasClicked.current = false; // important
        onChangeAutoComplete('');
        return;
      }

      // équivalent de "click + option exists"
      // (c’est plus robuste que: click, enter, ...)
      if (reason === 'selectOption') {
        userHasClicked.current = true;
        onClickAutoComplete(next);
        return;
      }

      // fallback (rare)
      onChangeAutoComplete(next);
    },
    [onClickAutoComplete, onChangeAutoComplete],
  );

  const autocompleteInputChanged = useCallback(
    async (event: React.SyntheticEvent, newInputValue: string, reason: string) => {
      // si ton champ est "forcedSelection" et déjà validé -> on bloque la saisie
      if (disabledField && reason === 'input') {
        event.stopPropagation();
        return;
      }

      // dès que l'utilisateur tape, on ré-autorise les recherches
      if (reason === 'input') userHasClicked.current = false;

      await onChangeAutoComplete(newInputValue);
    },
    [disabledField, onChangeAutoComplete],
  );

  const onHistoryTagChange = useCallback(
    (event: React.SyntheticEvent, nextValue: string) => {
      // équivalent d’un selectOption
      userHasClicked.current = true;
      onClickAutoComplete(nextValue);
    },
    [onClickAutoComplete],
  );

  const clearState = useCallback(() => {
    updateGlobalState('');
  }, [updateGlobalState]);

  const onBlur = useCallback(() => {
    setFieldFocused(undefined);
  }, [setFieldFocused]);

  // On retourne les valeurs à la vue
  return {
    textLocalValue,
    t,
    clearState,
    outlinedInputChanged,
    autocompleteChanged,
    autocompleteInputChanged,
    onHistoryTagChange,
    autoCompleteProp,
    isEventClick: userHasClicked.current,
    searchAllUO,
    setSearchAllUO,
    handleOnChangeSearchAllUO,
    searchNow,
    addFocus,
    textFieldRef,
    requiredField,
    verifRequiredField,
    fieldFocused,
    onBlur,
    lruList: list,
    showLruList: showList,
    toggleDisplayLruList,
    deleteLRUItem,
  };
};

export default useData;
